import React, { useState, useEffect, useRef } from 'react';
import moment from 'moment';
import {
    Box,
    Popover,
    Tabs,
    Tab,
    Button,
    IconButton,
    Typography,
    List,
    ListItem,
    ListItemText,
    Divider,
    useMediaQuery
} from '@material-ui/core';
import { useTheme, makeStyles } from '@material-ui/core/styles';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

const useStyles = makeStyles(theme => ({
    trigger: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        padding: '6px 12px',
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 8,
        cursor: 'pointer',
        backgroundColor: 'transparent',
        minWidth: 240,
        maxWidth: 320,
        transition: 'border-color 0.15s ease, background-color 0.15s ease',
        '&:hover': {
            borderColor: theme.palette.text.primary,
            backgroundColor: theme.palette.action?.hover || 'rgba(0,0,0,0.04)'
        }
    },
    triggerText: {
        flex: 1,
        fontSize: 13,
        fontWeight: 500,
        color: theme.palette.text.primary,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis'
    },
    triggerPlaceholder: {
        color: theme.palette.text.secondary,
        opacity: 0.7
    },
    triggerIcon: {
        fontSize: 18,
        color: theme.palette.text.secondary
    },
    popoverPaper: {
        width: 560,
        maxWidth: '95vw',
        borderRadius: 12,
        overflow: 'hidden',
        border: `1px solid ${theme.palette.divider}`,
        [theme.breakpoints.down('xs')]: {
            width: '95vw'
        }
    },
    container: {
        display: 'flex',
        flexDirection: 'row',
        [theme.breakpoints.down('xs')]: {
            flexDirection: 'column'
        }
    },
    presetsColumn: {
        width: 150,
        borderRight: `1px solid ${theme.palette.divider}`,
        padding: theme.spacing(1, 0),
        [theme.breakpoints.down('xs')]: {
            width: '100%',
            borderRight: 'none',
            borderBottom: `1px solid ${theme.palette.divider}`
        }
    },
    presetItem: {
        padding: '8px 16px',
        cursor: 'pointer',
        fontSize: 13,
        '&:hover': {
            backgroundColor: theme.palette.action?.hover || 'rgba(0,0,0,0.04)'
        }
    },
    presetItemActive: {
        backgroundColor: theme.palette.type === 'dark'
            ? 'rgba(0, 132, 137, 0.2)'
            : 'rgba(0, 132, 137, 0.1)',
        color: '#008489',
        fontWeight: 600,
        borderLeft: '3px solid #008489'
    },
    calendarColumn: {
        flex: 1,
        display: 'flex',
        flexDirection: 'column'
    },
    tabsRoot: {
        borderBottom: `1px solid ${theme.palette.divider}`,
        minHeight: 40
    },
    tab: {
        minHeight: 40,
        fontSize: 12,
        fontWeight: 600,
        textTransform: 'none',
        flex: 1
    },
    tabLabel: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
        textAlign: 'left'
    },
    tabTitle: {
        fontSize: 10,
        color: theme.palette.text.secondary,
        textTransform: 'uppercase',
        letterSpacing: 0.5
    },
    tabValue: {
        fontSize: 13,
        color: theme.palette.text.primary,
        fontWeight: 600
    },
    tabValuePlaceholder: {
        fontSize: 13,
        color: theme.palette.text.secondary,
        opacity: 0.6,
        fontWeight: 500
    },
    calendarBody: {
        padding: theme.spacing(1.5)
    },
    monthHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: theme.spacing(1),
        padding: '0 4px'
    },
    monthTitle: {
        fontSize: 14,
        fontWeight: 600,
        color: theme.palette.text.primary
    },
    navButton: {
        padding: 4,
        color: theme.palette.text.primary
    },
    weekdaysRow: {
        display: 'grid',
        gridTemplateColumns: 'repeat(7, 1fr)',
        marginBottom: 4
    },
    weekday: {
        textAlign: 'center',
        fontSize: 11,
        fontWeight: 600,
        color: theme.palette.text.secondary,
        padding: '4px 0',
        textTransform: 'uppercase'
    },
    daysGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(7, 1fr)',
        gap: 2
    },
    day: {
        aspectRatio: '1 / 1',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 12,
        cursor: 'pointer',
        borderRadius: 6,
        color: theme.palette.text.primary,
        transition: 'background-color 0.1s ease',
        userSelect: 'none',
        '&:hover': {
            backgroundColor: theme.palette.action?.hover || 'rgba(0,0,0,0.06)'
        }
    },
    dayOutsideMonth: {
        color: theme.palette.text.disabled,
        opacity: 0.4
    },
    dayInRange: {
        backgroundColor: theme.palette.type === 'dark'
            ? 'rgba(0, 132, 137, 0.25)'
            : 'rgba(0, 132, 137, 0.15)',
        borderRadius: 0,
        '&:hover': {
            backgroundColor: theme.palette.type === 'dark'
                ? 'rgba(0, 132, 137, 0.35)'
                : 'rgba(0, 132, 137, 0.25)'
        }
    },
    dayRangeStart: {
        backgroundColor: '#008489',
        color: '#fff',
        fontWeight: 600,
        borderTopRightRadius: 0,
        borderBottomRightRadius: 0,
        '&:hover': {
            backgroundColor: '#00696d'
        }
    },
    dayRangeEnd: {
        backgroundColor: '#008489',
        color: '#fff',
        fontWeight: 600,
        borderTopLeftRadius: 0,
        borderBottomLeftRadius: 0,
        '&:hover': {
            backgroundColor: '#00696d'
        }
    },
    daySingleSelected: {
        backgroundColor: '#008489',
        color: '#fff',
        fontWeight: 600,
        borderRadius: 6,
        '&:hover': {
            backgroundColor: '#00696d'
        }
    },
    dayToday: {
        fontWeight: 700,
        border: `1px solid ${theme.palette.text.primary}`
    },
    footer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: theme.spacing(1.5, 2),
        borderTop: `1px solid ${theme.palette.divider}`,
        gap: theme.spacing(1)
    },
    footerInfo: {
        fontSize: 12,
        color: theme.palette.text.secondary
    },
    footerActions: {
        display: 'flex',
        gap: theme.spacing(1)
    },
    btn: {
        textTransform: 'none',
        fontSize: 13,
        fontWeight: 600,
        minWidth: 80
    },
    applyBtn: {
        backgroundColor: '#008489',
        color: '#fff',
        '&:hover': {
            backgroundColor: '#00696d'
        },
        '&:disabled': {
            backgroundColor: theme.palette.action?.disabledBackground,
            color: theme.palette.action?.disabled
        }
    }
}));

const PRESETS = [
    { key: 'today', label: 'Today' },
    { key: 'yesterday', label: 'Yesterday' },
    { key: 'last7', label: 'Past 7 Days' },
    { key: 'last30', label: 'Past Month' },
    { key: 'last365', label: 'Past Year' }
];

const getPresetRange = (key) => {
    const now = moment.utc();
    switch (key) {
        case 'today':
            return { startDate: now.clone().startOf('day'), endDate: now.clone().endOf('day') };
        case 'yesterday': {
            const y = now.clone().subtract(1, 'day');
            return { startDate: y.clone().startOf('day'), endDate: y.clone().endOf('day') };
        }
        case 'last7':
            return {
                startDate: now.clone().subtract(6, 'days').startOf('day'),
                endDate: now.clone().endOf('day')
            };
        case 'last30':
            return {
                startDate: now.clone().subtract(29, 'days').startOf('day'),
                endDate: now.clone().endOf('day')
            };
        case 'last365':
            return {
                startDate: now.clone().subtract(364, 'days').startOf('day'),
                endDate: now.clone().endOf('day')
            };
        default:
            return null;
    }
};

const matchesPreset = (start, end) => {
    if (!start || !end) return null;
    for (const p of PRESETS) {
        const range = getPresetRange(p.key);
        if (
            range.startDate.isSame(start, 'day') &&
            range.endDate.isSame(end, 'day')
        ) {
            return p.key;
        }
    }
    return null;
};

function DateRangePickerOmniNew({ startDate, endDate, onDateChange, defaultNull, style }) {
    const classes = useStyles();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('xs'));
    const triggerRef = useRef(null);

    const [anchorEl, setAnchorEl] = useState(null);
    const [tab, setTab] = useState(0); // 0 = start, 1 = end
    const [draftStart, setDraftStart] = useState(startDate || null);
    const [draftEnd, setDraftEnd] = useState(endDate || null);
    const [viewMonth, setViewMonth] = useState(moment());

    const open = Boolean(anchorEl);

    // Sync draft when popover opens
    useEffect(() => {
        if (open) {
            setDraftStart(startDate || null);
            setDraftEnd(endDate || null);
            setTab(0);
            setViewMonth(startDate ? moment(startDate) : moment());
        }
    }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleOpen = (e) => setAnchorEl(e.currentTarget);
    const handleClose = () => setAnchorEl(null);

    const handlePresetClick = (key) => {
        const range = getPresetRange(key);
        setDraftStart(range.startDate);
        setDraftEnd(range.endDate);
        setViewMonth(range.startDate.clone());
        setTab(1);
    };

    const handleDayClick = (day) => {
        if (tab === 0) {
            // Setting start date
            setDraftStart(day.clone().startOf('day'));
            // If end is before new start, clear end
            if (draftEnd && day.isAfter(draftEnd, 'day')) {
                setDraftEnd(null);
            }
            setTab(1);
        } else {
            // Setting end date
            if (!draftStart) {
                // No start yet — treat this as start
                setDraftStart(day.clone().startOf('day'));
                return;
            }
            if (day.isBefore(draftStart, 'day')) {
                // If picking an end before start, swap
                setDraftEnd(draftStart.clone().endOf('day'));
                setDraftStart(day.clone().startOf('day'));
            } else {
                setDraftEnd(day.clone().endOf('day'));
            }
        }
    };

    const handleClear = () => {
        setDraftStart(null);
        setDraftEnd(null);
        setTab(0);
    };

  const handleApply = () => {
        if (draftStart && draftEnd) {
            onDateChange?.({
                startDate: moment.utc(moment(draftStart).format('YYYY-MM-DD')).startOf('day'),
                endDate: moment.utc(moment(draftEnd).format('YYYY-MM-DD')).endOf('day')
            });
        } else if (!draftStart && !draftEnd) {
            if (defaultNull) {
                onDateChange?.({ startDate: null, endDate: null });
            } else {
                onDateChange?.({
                    startDate: moment.utc().startOf('day'),
                    endDate: moment.utc().endOf('day')
                });
            }
        }
        handleClose();
    };

    const handleCancel = () => {
        handleClose();
    };

    // Trigger display text
    const triggerText = (() => {
        if (startDate && endDate) {
            const s = moment(startDate).format('MMM D, YYYY');
            const e = moment(endDate).format('MMM D, YYYY');
            if (s === e) return s;
            return `${s} – ${e}`;
        }
        return 'Select date range';
    })();

    const hasSelection = Boolean(startDate && endDate);
    const activePreset = matchesPreset(draftStart, draftEnd);

    // Calendar day rendering
    const renderCalendar = () => {
        const firstOfMonth = viewMonth.clone().startOf('month');
        const startOfGrid = firstOfMonth.clone().startOf('week');
        const endOfGrid = viewMonth.clone().endOf('month').endOf('week');

        const days = [];
        const cursor = startOfGrid.clone();
        while (cursor.isSameOrBefore(endOfGrid, 'day')) {
            days.push(cursor.clone());
            cursor.add(1, 'day');
        }

        const weekdays = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

        return (
            <div className={classes.calendarBody}>
                <div className={classes.monthHeader}>
                    <IconButton
                        size="small"
                        className={classes.navButton}
                        onClick={() => setViewMonth(viewMonth.clone().subtract(1, 'month'))}
                    >
                        <ChevronLeftIcon fontSize="small" />
                    </IconButton>
                    <Typography className={classes.monthTitle}>
                        {viewMonth.format('MMMM YYYY')}
                    </Typography>
                    <IconButton
                        size="small"
                        className={classes.navButton}
                        onClick={() => setViewMonth(viewMonth.clone().add(1, 'month'))}
                    >
                        <ChevronRightIcon fontSize="small" />
                    </IconButton>
                </div>

                <div className={classes.weekdaysRow}>
                    {weekdays.map(w => (
                        <div key={w} className={classes.weekday}>{w}</div>
                    ))}
                </div>

                <div className={classes.daysGrid}>
                    {days.map(d => {
                        const isOutside = !d.isSame(viewMonth, 'month');
                        const isToday = d.isSame(moment(), 'day');
                        const isStart = draftStart && d.isSame(draftStart, 'day');
                        const isEnd = draftEnd && d.isSame(draftEnd, 'day');
                        const isSingle = isStart && isEnd;
                        const inRange =
                            draftStart &&
                            draftEnd &&
                            d.isAfter(draftStart, 'day') &&
                            d.isBefore(draftEnd, 'day');

                        const dayClasses = [classes.day];
                        if (isOutside) dayClasses.push(classes.dayOutsideMonth);
                        if (isToday && !isStart && !isEnd) dayClasses.push(classes.dayToday);
                        if (inRange) dayClasses.push(classes.dayInRange);
                        if (isSingle) {
                            dayClasses.push(classes.daySingleSelected);
                        } else {
                            if (isStart) dayClasses.push(classes.dayRangeStart);
                            if (isEnd) dayClasses.push(classes.dayRangeEnd);
                        }

                        return (
                            <div
                                key={d.format('YYYY-MM-DD')}
                                className={dayClasses.join(' ')}
                                onClick={() => handleDayClick(d)}
                            >
                                {d.date()}
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    return (
        <div style={style}>
            <div
                ref={triggerRef}
                className={classes.trigger}
                onClick={handleOpen}
                role="button"
                tabIndex={0}
            >
                <CalendarTodayIcon className={classes.triggerIcon} />
                <span
                    className={`${classes.triggerText} ${!hasSelection ? classes.triggerPlaceholder : ''}`}
                >
                    {triggerText}
                </span>
                {hasSelection && (
                    <IconButton
                        size="small"
                        onClick={(e) => {
                            e.stopPropagation();
                            onDateChange?.(
                                defaultNull
                                    ? { startDate: null, endDate: null }
                                    : {
                                        startDate: moment.utc().startOf('day'),
                                        endDate: moment.utc().endOf('day')
                                    }
                            );
                        }}
                        style={{ padding: 2 }}
                    >
                        <CloseIcon style={{ fontSize: 14 }} />
                    </IconButton>
                )}
            </div>

            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                PaperProps={{ className: classes.popoverPaper }}
            >
                <div className={classes.container}>
                    {/* Presets */}
                    <div className={classes.presetsColumn}>
                        {PRESETS.map(p => (
                            <div
                                key={p.key}
                                className={`${classes.presetItem} ${activePreset === p.key ? classes.presetItemActive : ''}`}
                                onClick={() => handlePresetClick(p.key)}
                            >
                                {p.label}
                            </div>
                        ))}
                    </div>

                    {/* Calendar */}
                    <div className={classes.calendarColumn}>
                        <Tabs
                            value={tab}
                            onChange={(_, v) => setTab(v)}
                            className={classes.tabsRoot}
                            indicatorColor="primary"
                            TabIndicatorProps={{ style: { backgroundColor: '#008489' } }}
                            variant="fullWidth"
                        >
                            <Tab
                                className={classes.tab}
                                label={
                                    <div className={classes.tabLabel}>
                                        <span className={classes.tabTitle}>Start Date</span>
                                        <span className={draftStart ? classes.tabValue : classes.tabValuePlaceholder}>
                                            {draftStart ? moment(draftStart).format('MMM D, YYYY') : 'Select'}
                                        </span>
                                    </div>
                                }
                            />
                            <Tab
                                className={classes.tab}
                                label={
                                    <div className={classes.tabLabel}>
                                        <span className={classes.tabTitle}>End Date</span>
                                        <span className={draftEnd ? classes.tabValue : classes.tabValuePlaceholder}>
                                            {draftEnd ? moment(draftEnd).format('MMM D, YYYY') : 'Select'}
                                        </span>
                                    </div>
                                }
                            />
                        </Tabs>

                        {renderCalendar()}
                    </div>
                </div>

                <Divider />
                <div className={classes.footer}>
                    <Button
                        onClick={handleClear}
                        size="small"
                        className={classes.btn}
                    >
                        Clear
                    </Button>
                    <div className={classes.footerActions}>
                        <Button
                            onClick={handleCancel}
                            size="small"
                            variant="outlined"
                            className={classes.btn}
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleApply}
                            size="small"
                            variant="contained"
                            className={`${classes.btn} ${classes.applyBtn}`}
                            disabled={!draftStart || !draftEnd}
                        >
                            Apply
                        </Button>
                    </div>
                </div>
            </Popover>
        </div>
    );
}

export default DateRangePickerOmniNew;